import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, setSeo, track } from "@/lib/api";
import { SmartImage } from "@/components/site/SmartImage";

export default function ToolsIndex() {
  const [tools, setTools] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSeo({
      title: "Planning Tools — Human Home Living",
      description:
        "Two interactive planning tools: check whether a home golf simulator fits your room, and estimate the backup power your household actually needs.",
    });
    track({ event_type: "page_view", page: "/tools" });
    api.get("/tools").then((r) => setTools(r.data));
  }, []);

  return (
    <div data-testid="tools-index-page">
      <header className="bg-white border-b border-line px-5 md:px-8 pt-16 pb-14">
        <div className="max-w-[1080px] mx-auto">
          <span className="eyebrow">Interactive</span>
          <h1 className="display text-[42px] md:text-[60px] leading-[1.03] mt-5 text-charcoal max-w-3xl">
            Planning tools
          </h1>
          <p className="text-[19px] md:text-[23px] font-light leading-[1.5] text-[#4F4F4F] mt-6 max-w-2xl">
            Each tool starts from your actual room, your actual loads or your actual household — then
            tells you what that means for the decision in front of you.
          </p>
        </div>
      </header>

      <section className="px-5 md:px-8 py-14 md:py-18">
        <div className="max-w-[1080px] mx-auto space-y-12 md:space-y-14">
          {tools.map((t, i) => (
            <Link
              key={t.slug}
              to={`/tools/${t.slug}`}
              data-testid={`tools-index-card-${t.slug}`}
              className="group grid lg:grid-cols-[52fr_48fr] gap-6 lg:gap-10 items-center"
            >
              <div className="overflow-hidden aspect-[16/9] bg-bone">
                <SmartImage
                  src={t.image}
                  alt={t.image_alt || t.name}
                  sizes="(min-width:1024px) 52vw, 100vw"
                  maxWidth={1280}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-3.5">
                  <span className="display text-[19px] text-sage/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[11.5px] tracking-[0.14em] uppercase font-medium text-[#48563F] bg-sage-soft border border-sage/35 rounded-full px-3 py-1">
                    {t.barrier}
                  </span>
                </div>
                <h2 className="display text-[27px] md:text-[33px] leading-[1.14] text-charcoal group-hover:text-sage-dark transition-colors duration-300">
                  {t.name}
                </h2>
                <p className="text-[16px] md:text-[17.5px] leading-[1.6] text-[#454545] mt-3 max-w-xl">
                  {t.summary}
                </p>
                <span className="inline-flex items-center gap-2 mt-6 text-[12.5px] tracking-[0.14em] uppercase font-semibold text-white bg-sage group-hover:bg-sage-dark transition-colors duration-300 px-6 py-3.5">
                  {t.cta_label} <ArrowRight size={15} />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <p className="max-w-[1080px] mx-auto text-[15px] text-[#454545] mt-12">
          Every tool result is planning guidance. Product matching is driven by fit, compatibility and
          verified specifications — never by which merchant pays the most.
        </p>
      </section>
    </div>
  );
}
